import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

/*
  CJCEventBannerPopup
  - Sticky banner top with "Notify Me" button
  - Center modal dialog with form that POSTs to Web3Forms
  - Validation: require Email OR Phone
  - Frequency cap via localStorage key `cjc_if2026_seen_at`
  - Honeypot `company` hidden field
  - Capture utm_source, utm_campaign and document.referrer
  - Accessible markup: role="dialog", aria-label, ESC & backdrop to close
  - Tailwind utility classes for styling
*/

const STORAGE_KEY = 'cjc_if2026_seen_at';

export default function CJCEventBannerPopup({
  web3formsAccessKey,
  redirectUrl,
  openOnLoad = true,
  autoOpen,
  useSessionStorage = false,
}) {
  // UI state
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // form state
  const [fullName, setFullName] = useState('');
  const [club, setClub] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [interests, setInterests] = useState({
    handler: false,
    breeder: false,
    volunteering: false,
    vendor: false,
  });
  const [companyHoneypot, setCompanyHoneypot] = useState('');

  // validation state
  const [validationError, setValidationError] = useState('');

  // refs for focus management
  const triggerRef = useRef(null);
  const modalRef = useRef(null);

  // parse UTM params and referrer
  const [utmSource, setUtmSource] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [referrer, setReferrer] = useState('');

  // Effect: capture utm and referrer once
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      setUtmSource(params.get('utm_source') || '');
      setUtmCampaign(params.get('utm_campaign') || '');
    } catch (e) {
      // ignore
    }
    setReferrer(document.referrer || '');
  }, []);

  // Frequency cap helper
  const markSeenNow = useCallback(() => {
    try { 
      if (useSessionStorage) {
        sessionStorage.setItem(STORAGE_KEY, 'seen');
      } else {
        localStorage.setItem(STORAGE_KEY, String(Date.now())); 
      }
    } catch(e){}
    setSeen(true);
  }, [useSessionStorage]);

  const checkCap = useCallback(() => {
    try {
      if (useSessionStorage) {
        return sessionStorage.getItem(STORAGE_KEY) === 'seen';
      } else {
        const v = localStorage.getItem(STORAGE_KEY);
        return v ? true : false; // If any value exists, consider it seen
      }
    } catch (e) {
      return false;
    }
  }, [useSessionStorage]);

  // Auto-open logic
  useEffect(() => {
    if (!openOnLoad && !autoOpen) return;
    const already = checkCap();
    if (already) {
      setSeen(true);
      return;
    }

    const openModal = () => {
      setOpen(true);
      markSeenNow();
    };

    if (autoOpen && typeof autoOpen.delayMs === 'number') {
      const t = setTimeout(openModal, autoOpen.delayMs);
      return () => clearTimeout(t);
    }

    if (openOnLoad) {
      openModal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOnLoad, autoOpen, checkCap, markSeenNow]);

  // Close helpers
  const close = useCallback(() => {
    setOpen(false);
    // return focus to trigger
    if (triggerRef.current) triggerRef.current.focus();
  }, []);

  // keyboard: ESC to close and basic focus trap
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        if (open) close();
      }
      if (e.key === 'Tab' && open && modalRef.current) {
        // focus trap: keep tabbing inside modal
        const focusable = modalRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          (last).focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          (first).focus();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  // open via banner button
  const openFromBanner = () => {
    setOpen(true);
    markSeenNow();
  };

  // toggle interest checkboxes
  const toggleInterest = (key) => {
    setInterests((p) => ({ ...p, [key]: !p[key] }));
  };

  // validation: require either email or phone
  function validate() {
    setValidationError('');
    if (!email.trim() && !phone.trim()) {
      setValidationError('Please provide either an email address or a phone number.');
      return false;
    }
    // basic email format check if provided
    if (email.trim()) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!re.test(email.trim())) {
        setValidationError('Please enter a valid email address.');
        return false;
      }
    }
    return true;
  }

  // submit handler
  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    // honeypot: if filled, silently accept and reset
    if (companyHoneypot && companyHoneypot.trim() !== '') {
      // pretend success
      setFullName(''); setClub(''); setEmail(''); setPhone(''); setInterests({handler:false,breeder:false,volunteering:false,vendor:false}); setCompanyHoneypot('');
      setSuccessMessage("Thanks! You're on the list.");
      markSeenNow();
      return;
    }

    if (!validate()) return;

    if (!web3formsAccessKey) {
      setErrorMessage('Form configuration error: missing access key. Please email info@cjccombine.com');
      return;
    }

    setSubmitting(true);
    try {
      const selected = Object.entries(interests)
        .filter(([, v]) => v)
        .map(([k]) => k.charAt(0).toUpperCase() + k.slice(1))
        .join(', ');

      const formData = new FormData();
      formData.append('access_key', web3formsAccessKey);
      formData.append('subject', 'IF Convention 2026 – New Interest Signup');
      formData.append('from_name', fullName || '');
      formData.append('fullName', fullName);
      formData.append('club', club);
      formData.append('email', email);
      formData.append('phone', phone);
      formData.append('interests', selected);
      if (utmSource) formData.append('utm_source', utmSource);
      if (utmCampaign) formData.append('utm_campaign', utmCampaign);
      if (referrer) formData.append('referrer', referrer);
      if (redirectUrl) formData.append('redirect', redirectUrl);

      const resp = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });

      const json = await resp.json();
      if (json.success) {
        setSuccessMessage("Thanks! You're on the list.");
        // reset form
        setFullName(''); setClub(''); setEmail(''); setPhone(''); setInterests({handler:false,breeder:false,volunteering:false,vendor:false});
        markSeenNow();
      } else {
        setErrorMessage((json.message && String(json.message)) || 'Submission failed. Please try again or email info@cjccombine.com');
      }
    } catch (err) {
      setErrorMessage('Submission error. Please try again or email info@cjccombine.com');
    } finally {
      setSubmitting(false);
    }
  };

  // small helper: input classes
  const inputCls = 'w-full px-4 py-3 rounded-md border border-gray-300 text-base focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#0b3d91]';

  return (
    <>
      {/* Featured IF Convention Section - Always Visible */}
      <Link 
        to="/if-convention-2026" 
        className="text-decoration-none d-block mb-4"
      >
        <div className="jumbotron jumbotron-fluid" style={{ 
          background: 'linear-gradient(135deg, #0b3d91 0%, #1560a8 100%)', 
          color: 'white',
          border: '3px solid #ffd700',
          borderRadius: '10px',
          boxShadow: '0 4px 15px rgba(11, 61, 145, 0.3)'
        }}>
          <div className="container text-center">
            <h2 className="display-4 fw-bold mb-3" style={{ fontSize: '3rem', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
              🏆 2026 IF Convention Race
            </h2>
            <h3 className="h4 mb-3" style={{ color: '#ffd700', fontWeight: '600' }}>
              The 145th IF Convention
            </h3>
            <p className="lead mb-3" style={{ fontSize: '1.3rem', fontWeight: '500' }}>
              Hosted by the Central Jersey Combine
            </p>
            <div className="btn btn-warning btn-lg px-5 py-3" style={{ 
              fontSize: '1.2rem', 
              fontWeight: 'bold',
              boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
              border: 'none'
            }}>
              Get Details →
            </div>
          </div>
        </div>
      </Link>

      {/* Backdrop and Modal (Bootstrap markup so styles apply without Tailwind) */}
      {open && (
        <>
          <div className="modal-backdrop show" onClick={close} />

          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true" aria-label="IF Convention 2026 announcement">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header border-bottom-0 pb-0">
                  <h4 className="modal-title text-primary display-6 fw-bold">2026 IF Convention</h4>
                  <button type="button" className="btn-close" aria-label="Close" onClick={close}></button>
                </div>
                <div className="modal-body pt-2">
                  <h5 className="text-secondary mb-4 fs-4">The 145th IF Convention</h5>
                  <p className="fs-5 fw-semibold text-dark mb-4">Hosted by the Central Jersey Combine</p>
                  <div className="border-top pt-4">
                    <p className="fs-6 text-muted mb-3">Be the first to receive:</p>
                    <ul className="list-unstyled mb-4">
                      <li className="d-flex align-items-center mb-3">
                        <span className="text-primary me-2" style={{ fontSize: '0.5rem' }}>●</span>
                        <span className="fs-6">Race details and schedules</span>
                      </li>
                      <li className="d-flex align-items-center mb-3">
                        <span className="text-primary me-2" style={{ fontSize: '0.5rem' }}>●</span>
                        <span className="fs-6">Handlers list</span>
                      </li>
                      <li className="d-flex align-items-center">
                        <span className="text-primary me-2" style={{ fontSize: '0.5rem' }}>●</span>
                        <span className="fs-6">Important event updates</span>
                      </li>
                    </ul>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-light" onClick={close}>
                      Cancel
                    </button>
                    <a href="/if-convention-2026" className="btn btn-primary" style={{ background: '#0b3d91', borderColor: '#0b3d91' }}>
                      Learn More
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
